import { h, defineComponent, ref, SetupContext } from 'vue';
import { ValidationError } from '@/entities/validation';
import { SelectOptions, SelectOptionsWithTooltip, SetterCallback } from '@/ui/types';
import { Field, FieldProps } from './Field';
import { TagInput, TagInputProps } from '../controls/TagInput';
import { StackableTagInput } from '../controls/StackableTagInput';
import { Icon, IconProps } from '@/ui/components/icon/Icon';

export interface TagFieldProps<T = number> {
  name: string;
  label: string;
  value: T[];
  icon?: string;
  required: boolean;
  placeholder: string;
  disabled: boolean;
  options: SelectOptions<T> | SelectOptionsWithTooltip<T>;
  errors?: ValidationError[];
  hideLabel?: boolean;
  horizontal?: boolean;
  groupField?: string;
  groupOptions?: string;
  inputName?: string;
  tooltipText?: string;
  overrideErrorMessage?: string;
  isStackable?: boolean;
  maxTags?: number;
  maxListedOptions?: number;
}

export const tagFieldProps = [
  'name', 'label', 'value', 'icon', 'required', 'placeholder', 'options', 'errors', 'disabled',
  'hideLabel', 'horizontal', 'showDropdownIcon', 'groupField', 'groupOptions', 'inputName', 'isStackable',
  'tooltipText', 'overrideErrorMessage', 'maxTags', 'maxListedOptions',
];

export const TagField = defineComponent<TagFieldProps>({
  name: 'TagField',
  props: tagFieldProps,
  setup(props, context: SetupContext) {
    const isOpened = ref<boolean>(false);

    return function render() {
      const fieldProps: FieldProps = {
        label: props.label,
        name: props.name,
        required: props.required,
        errors: props.errors,
        hideLabel: props.hideLabel,
        horizontal: props.horizontal,
        tooltipText: props.tooltipText,
        overrideErrorMessage: props.overrideErrorMessage,
      };

      const tagInputProps: TagInputProps<number> = {
        value: props.value,
        options: props.options,
        disabled: props.disabled,
        icon: props.icon,
        placeholder: props.value.length === 0 ? props.placeholder : '',
        groupField: props.groupField,
        groupOptions: props.groupOptions,
        name: `${props.name}Input`,
        maxTags: props.maxTags,
        maxListedOptions: props.maxListedOptions,
      };

      const iconProps: IconProps = {
        icon: 'chevron-down',
        size: 'xs',
        rotation: isOpened.value ? 180 : null,
        iconPack: 'far',
      };

      return h(Field, {
        props: fieldProps,
        class: ['tag-field', props.hideLabel ? 'tag-field--hidden-label' : ''],
      }, [
        h(props.isStackable ? StackableTagInput : TagInput, {
          props: tagInputProps,
          scopedSlots: context.slots,
          class: { 'taginput--stackable': props.isStackable },
          on: {
            input: (value: unknown[]) => {
              context.emit('input', value);
            },
            focus: () => {
              isOpened.value = true;
            },
            blur: () => {
              isOpened.value = false;
            },
            typing: (e: string) => {
              context.emit('typing', e);
            },
          },
        }),
        h('div', {
          class: ['icon tag-field__icon'],
        }, [
          h(Icon, {
            props: iconProps,
          }),
        ]),
      ]);
    };
  },
  mounted() {
    // Since we don't have a slot for appending the dropdown icon
    // into this component we need to manipulate the DOM
    // to move from its original position into the
    // insider container where it's supposed to be
    const element = this.$el;
    const tagInputContainer = element.querySelector('.taginput.control')!;
    const icon = element.querySelector('.tag-field__icon.icon');

    tagInputContainer.appendChild(icon!);
  },
});

export function tagField<T>(props: TagFieldProps<T>, input: SetterCallback<T[]>) {
  return h(TagField, {
    props,
    on: { input },
  });
}
